import * as React from 'react';

import { BulkOperationDialog } from './BulkOperationDialog';
import { FirmSelectionTable } from './FirmSelectionTable';
import { ServiceTogglePanel } from './ServiceTogglePanel';
import { useBulkSOWOperations } from './useBulkSOWOperations';

interface FirmBasic {
  id: string;
  name: string;
}

export const BulkSOWEditor: React.FC = () => {
  const [selectedFirmIds, setSelectedFirmIds] = React.useState<Set<string>>(
    new Set(),
  );
  const [allFirms, setAllFirms] = React.useState<FirmBasic[]>([]);
  const [pendingChanges, setPendingChanges] = React.useState<
    Map<string, 'enable' | 'disable'>
  >(new Map());
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const {
    services,
    isLoadingCatalogue,
    firmServiceCache,
    loadServicesForFirms,
    computeAggregateStates,
    executeBulkChanges,
    operationState,
    resetOperationState,
  } = useBulkSOWOperations();

  // Get selected firms as array
  const selectedFirms = React.useMemo(() => {
    return allFirms.filter((f) => selectedFirmIds.has(f.id));
  }, [allFirms, selectedFirmIds]);

  // Load firm services when selection changes
  React.useEffect(() => {
    if (selectedFirms.length > 0) {
      loadServicesForFirms(selectedFirms);
    }
  }, [selectedFirms, loadServicesForFirms]);

  // Compute aggregate states for services
  const aggregateStates = React.useMemo(() => {
    return computeAggregateStates(selectedFirmIds);
  }, [computeAggregateStates, selectedFirmIds, firmServiceCache]);

  const handleFirmsLoaded = React.useCallback((firms: FirmBasic[]) => {
    setAllFirms(firms);
  }, []);

  const handleToggleChange = React.useCallback(
    (serviceName: string, action: 'enable' | 'disable' | null) => {
      setPendingChanges((prev) => {
        const newMap = new Map(prev);
        if (action === null) {
          newMap.delete(serviceName);
        } else {
          newMap.set(serviceName, action);
        }
        return newMap;
      });
    },
    [],
  );

  const handleApply = React.useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleConfirm = React.useCallback(async () => {
    await executeBulkChanges(selectedFirms, pendingChanges);
  }, [executeBulkChanges, selectedFirms, pendingChanges]);

  const handleDone = React.useCallback(() => {
    setDialogOpen(false);
    setPendingChanges(new Map());
    resetOperationState();
    // Optionally clear selection
    // setSelectedFirmIds(new Set());
  }, [resetOperationState]);

  return (
    <div className="flex h-full">
      {/* Left panel - Firm selection */}
      <div className="w-1/2 border-r border-zinc-200 dark:border-zinc-800">
        <FirmSelectionTable
          selectedFirms={selectedFirmIds}
          onSelectionChange={setSelectedFirmIds}
          onFirmsLoaded={handleFirmsLoaded}
        />
      </div>

      {/* Right panel - Service toggles */}
      <div className="w-1/2">
        <ServiceTogglePanel
          services={aggregateStates}
          selectedCount={selectedFirmIds.size}
          pendingChanges={pendingChanges}
          onToggleChange={handleToggleChange}
          onApply={handleApply}
          isLoading={
            isLoadingCatalogue || operationState.status === 'processing'
          }
        />
      </div>

      {/* Confirmation/Progress dialog */}
      <BulkOperationDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open && operationState.status !== 'processing') {
            setDialogOpen(false);
          }
        }}
        firms={selectedFirms}
        changes={pendingChanges}
        operationState={operationState}
        onConfirm={handleConfirm}
        onDone={handleDone}
      />
    </div>
  );
};
